import { channel } from "redux-saga";
import {
  call,
  put,
  select,
  StrictEffect,
  take,
  takeEvery
} from "redux-saga/effects";

import { DataType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";

import { performDataAction } from "@/services/api";
import { selectActiveClientId } from "@/store/features/companies/companies";

import {
  UploadDocumentActions,
  uploadDocuments,
  uploadDocumentsFailure,
  uploadDocumentSuccess
} from "./documentsUpload";

/**
 * A Redux-Saga channel used to pass File objects asynchronously.
 *
 * Lifecycle:
 * - The channel is created once and remains active for the lifetime of the application.
 * - File objects are sent to this channel from other parts of the application (e.g., UI components or middleware).
 * - The `handleDocumentUpload` saga listens to this channel using `take(fileUploadChannel)` to receive the File objects.
 *
 * Synchronization Flow:
 * - Producers (e.g., UI components) send File objects to the channel using `fileUploadChannel.put(files)`.
 * - The `handleDocumentUpload` saga acts as a consumer, waiting for and processing the File objects.
 * - This ensures a decoupled and asynchronous flow between the producer and consumer.
 */
export const fileUploadChannel = channel<Array<File>>();

export function* handleDocumentUpload(
  action: UploadDocumentActions
): Generator<StrictEffect, void, any> {
  const { files } = action.payload;
  const { onSuccess } = action.meta;
  const clientId = yield select(selectActiveClientId);

  try {
    // Wait for the File objects from the component
    const fileObjects: Array<File> = yield take(fileUploadChannel);

    const formData = new FormData();
    fileObjects.forEach((file) => {
      formData.append("files", file, file.name);
    });

    yield call(
      performDataAction,
      DataType.UploadDocuments,
      "create",
      { companyId: clientId },
      undefined,
      formData
    );

    yield put(uploadDocumentSuccess(files, onSuccess));
  } catch (error) {
    yield put(uploadDocumentsFailure(extractError(error)));
  }
}

export default function* documentUploadSaga() {
  yield takeEvery(uploadDocuments.type, handleDocumentUpload);
}
